import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../CustomControls/dropdown";
// import "../../assets/css/PrepregnancyDetails.css";

const PrepregnancyDetails = ({
  prevStep,
  formData,
  handleChange,
  handleSubmit,
  isSubmitting,
  error,
  setErrors,
  step, totalSteps, isHighmarkPortal
}) => {
  const { t } = useTranslation();

  const initialIsMetric = formData.TypeofHeight !== "inch" && formData.TypeofWeigh !== "lbs";
  const [isMetric, setIsMetric] = useState(initialIsMetric);
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [heightCm, setHeightCm] = useState(formData.Height || "");
  const [weight, setWeight] = useState(formData.Weight || "");

  useEffect(() => {
    if (!formData.TypeofHeight || !formData.TypeofWeigh) {
      const heightUnit = initialIsMetric ? "cm" : "inch";
      const weightUnit = initialIsMetric ? "kgs" : "lbs";
      handleChange({ target: { name: "TypeofHeight", value: heightUnit } });
      handleChange({ target: { name: "TypeofWeigh", value: weightUnit } });
    }
  
    // Restore previous values from formData (for feet/inch case)
    if (formData.heightFeet) {
      setHeightFeet(formData.heightFeet);
    }
  
    if (formData.heightInches) {
      setHeightInches(formData.heightInches);
    }
  
    if (formData.TypeofHeight === "cm" && formData.Height) {
      setHeightCm(formData.Height);
    }
  
    if (formData.Weight) {
      setWeight(formData.Weight);
    }
  }, []);
  

  const validateWeight = (value) => {
    return value === "" || /^(?!\.)\d{0,3}(\.\d{0,2})?$/.test(value);
  };

  const handleWeightChange = (e) => {
    let value = e.target.value;
    if (validateWeight(value)) {
      setWeight(value);
      handleChange({ target: { name: "Weight", value } });
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        if (value) delete updatedErrors.Weight;
        return updatedErrors;
      });
    }
    if (value === "") {
      setWeight("");
      handleChange({ target: { name: "Weight", value: "" } });
    }
  };

  const handleHeightCmChange = (e) => {
    let value = e.target.value;
    if (/^(?!\.)\d{0,3}(\.\d{0,2})?$/.test(value)) {
      setHeightCm(value);
      handleChange({ target: { name: "Height", value } });
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        if (value) delete updatedErrors.Height;
        return updatedErrors;
      });
    }
  };

  const handleToggle = () => {
    const newIsMetric = !isMetric;
    setIsMetric(newIsMetric);
  
    // Reset values in formData
    handleChange({ target: { name: "TypeofHeight", value: newIsMetric ? "cm" : "inch" } });
    handleChange({ target: { name: "TypeofWeigh", value: newIsMetric ? "kgs" : "lbs" } });
    handleChange({ target: { name: "Height", value: "" } });
    handleChange({ target: { name: "Weight", value: "" } });
  
    // Reset local component state
    setHeightFeet("");
    setHeightInches("");
    setHeightCm("");
    setWeight("");
  
    // Optionally, clear any validation errors
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.Height;
      delete updatedErrors.Weight;
      return updatedErrors;
    });
  };
  
  

  const renderHeightInput = () => {
    if (isMetric) {
      return (
        <input
          type="text"
          className="form-control win-form-control w-100-percent"
          value={heightCm}
          onChange={handleHeightCmChange}
          placeholder="Enter height"
          maxLength={6}
        />
      );
    } else {
      const feetOptions = Array.from({ length: 9 }, (_, i) => `${i+1}`);
      const inchOptions = Array.from({ length: 12 }, (_, i) => `${i}`);

      return (
        <div className="d-flex flex-column">
          <div className="d-flex gap-3">
            <CustomDropdown
              options={feetOptions}
              selectedOption={heightFeet}
              onChange={(ft) => {
                setHeightFeet(ft);
                handleChange({ target: { name: "heightFeet", value: ft } });
              
                if (ft !== "" && heightInches !== "") {
                  handleChange({ target: { name: "Height", value: `${ft}.${heightInches}` } });
                }
              }}
              
              placeholder="Select feet"
              searchable={false}
              disabled={false}
            />
            <CustomDropdown
              options={inchOptions}
              selectedOption={heightInches}
              onChange={(inch) => {
                setHeightInches(inch);
                handleChange({ target: { name: "heightInches", value: inch } });
              
                if (heightFeet !== "" && inch !== "") {
                  handleChange({ target: { name: "Height", value: `${heightFeet}.${inch}` } });
                }
              }}
                    
              placeholder="Select inches"
              searchable={false}
              disabled={false}
            />
          </div>
          
            {heightFeet && heightInches ? (
              <div className="form-label win-form-label d-flex justify-content-end mt-2">
                <span className="text-muted">{heightFeet} Feet {heightInches} Inches</span>
              </div>
            ) : (heightFeet || heightInches) && !error?.Height ? (
              <span className="text-muted error-text text-danger">Please select both feet and inches to complete your height</span>
            ) : null}
          

        </div>
      );
    }
  };

  return (
    <div className="step-section">
      <h3 className="page-heading-secondary-black-500 mb-5">
        {t("prepregnancydetails")}
      </h3>

      {/* Unit Toggle */}
      <div className="mb-5 row switch-control-wrapper align-items-center">
        <div className="col-sm-12 d-flex align-items-center">
          <label className="switch me-2 mb-0">
            <input
              type="checkbox"
              className="switch-input"
              checked={!isMetric}
              onChange={handleToggle}
            />
            <span className="slider round"></span>
          </label>
          <span className="toggle-label">
            {isMetric ? "Convert to (ft/inch)/lbs" : "Convert to cm/kgs"}
          </span>
        </div>
      </div>

    
      {/* Height Field */}
      <div className="mb-3 d-flex flex-column" style={{minHeight: "90px"}}>
        <span className="form-label win-form-label">Height</span>
        {renderHeightInput()}
        {/* {error?.Height && (
          <span className="error-text text-danger mb-3">{error.Height}</span>
        )} */}
      </div>

  {/* Weight Field */}
  <div className="mb-3 d-flex flex-column">
        <span className="form-label win-form-label">Weight</span>
        <input
          type="text"
          className="form-control win-form-control w-100-percent"
          value={weight}
          onChange={handleWeightChange}
          placeholder="Enter weight"
          maxLength={6}
        />
        {/* {error?.Weight && (
          <span className="error-text text-danger">{error.Weight}</span>
        )} */}
      </div>

      {/* Buttons */}
      <div className="mt-5 mb-5">
  {isHighmarkPortal ? (
    <div className="pagination-wrapper mt-5">
      <button className="previous-btn" onClick={prevStep}></button>
      <span className="me-3">{step} of {totalSteps}</span>
      <button className="next-btn" onClick={handleSubmit} disabled={isSubmitting}></button>
    </div>
  ) : (
    <>
      <button
        className="win-btn-secondary mobile-view me-3 mb-2 mb-md-0"
        onClick={prevStep}
      >
        {t("back")}
      </button>
      <button
        className="win-btn-primary mobile-view"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {t("healthprofile")}
      </button>
    </>
  )}
</div>

    </div>
  );
};

export default PrepregnancyDetails;
